import { SVGProps } from 'react';
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from 'recharts';
import colors from 'tailwindcss/colors';
import { Coordinate } from 'recharts/types/util/types';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@antimatterhq/ui/components/chart';
import { cn } from '@antimatterhq/ui/lib/utils';

export type BarChartProps = {
  data: { [key: string]: unknown }[];
  dataKey: string;
  className?: string;
  color?: string;
  activeColor?: string;
  barRadius?: number;
  chartConfig?: ChartConfig;
  /**
   * X-axis configuration. If not provided, x-axis will not be rendered.
   * @example
   * {
   *   dataKey: 'month',
   *   showGrid: true,
   *   tickFormatter: (value) => value.toUpperCase()
   * }
   */
  xAxis?: {
    /** Key from data object for x-axis labels */
    dataKey: string;
    /** Whether to show vertical grid lines */
    showGrid?: boolean;
    /** Function to format tick labels */
    tickFormatter?: (value: string) => string;
  };
  /**
   * Y-axis configuration. If not provided, y-axis will not be rendered.
   * @example
   * {
   *   dataKey: 'value',
   *   showGrid: true,
   *   tickFormatter: (value) => `$${value}`
   * }
   */
  yAxis?: {
    /** Key from data object for y-axis values */
    dataKey: string;
    /** Whether to show horizontal grid lines */
    showGrid?: boolean;
    /** Function to format tick labels */
    tickFormatter?: (value: string) => string;
  };
  /**
   * Tooltip configuration. By default, the tooltip is not shown.
   * Set to true to show a basic tooltip with default formatting or
   * provide an object with custom configuration.
   *
   * @example Basic styling for cursor
   * {
   *   hideLabel: false,
   *   cursor: true,
   *   formatter: (value) => `$${value}`
   * }
   *
   * @example Pass custom styling to cursor
   * {
   *   hideLabel: false,
   *   cursor: { fill: colors.slate[800], radius: 4, opacity: 0.2 },
   *   formatter: (value) => `$${value}`
   * }
   */
  tooltip?:
    | true
    | {
        hideLabel?: boolean;
        cursor?: boolean | SVGProps<SVGElement>;
        formatter?: (value: unknown) => React.ReactNode;
        position?: Partial<Coordinate>;
      };
};

/**
 * A customizable bar chart component built on Recharts
 *
 * @example
 * <BarChart
 *   data={[{ month: 'Jan', sales: 100 }, { month: 'Feb', sales: 200 }]}
 *   dataKey="sales"
 * />
 */
export const BarChart = ({
  data,
  dataKey,
  className,
  color = colors.blue[300],
  activeColor = colors.blue[700],
  chartConfig = {},
  xAxis,
  yAxis,
  tooltip,
  barRadius = 4,
}: BarChartProps) => {
  return (
    <ChartContainer
      config={chartConfig}
      className={cn('min-h-[10px] w-full', className)}
    >
      <RechartsBarChart accessibilityLayer data={data}>
        {xAxis?.showGrid && <CartesianGrid vertical={false} />}
        {yAxis?.showGrid && <CartesianGrid horizontal={false} />}

        {xAxis && (
          <XAxis
            dataKey={xAxis.dataKey}
            tickLine={false}
            tickMargin={10}
            axisLine={false}
            tickFormatter={xAxis?.tickFormatter}
          />
        )}

        {yAxis && (
          <YAxis
            dataKey={yAxis.dataKey}
            tickLine={false}
            tickMargin={10}
            axisLine={false}
            tickFormatter={yAxis?.tickFormatter}
          />
        )}

        {tooltip === true ? (
          <ChartTooltip
            content={
              <ChartTooltipContent className="bg-white dark:bg-grey-800" />
            }
          />
        ) : (
          tooltip && (
            <ChartTooltip
              cursor={tooltip?.cursor ?? false}
              position={tooltip?.position}
              content={
                <ChartTooltipContent
                  className="bg-white dark:bg-grey-800"
                  hideLabel={tooltip?.hideLabel}
                  formatter={tooltip?.formatter}
                />
              }
            />
          )
        )}
        <Bar
          dataKey={dataKey}
          fill={color}
          activeBar={{ fill: activeColor }}
          radius={barRadius}
        />
      </RechartsBarChart>
    </ChartContainer>
  );
};
